<template>
  <div style="min-height: var(--app-viewport-height, 100vh); background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);">
    <div
      v-if="showLoader"
      style="position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.8);"
    >
      <v-progress-circular color="#23A5E6" indeterminate :size="64" :width="6"></v-progress-circular>
    </div>

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <v-btn
                  icon
                  variant="text"
                  color="white"
                  class="mr-2"
                  @click="goBack"
                >
                  <v-icon size="24">mdi-arrow-left</v-icon>
                </v-btn>

                <v-avatar size="40" class="mr-3" style="border: 2px solid rgba(255,255,255,0.3);">
                  <v-img :src="selectedUser?.prependAvatar" :alt="selectedUser?.title" />
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="text-subtitle-1 font-weight-semibold" style="color: #ffffff;">
                    {{ selectedUser?.title || 'User' }}
                  </div>
                </div>

                <v-btn
                  icon
                  variant="text"
                  color="white"
                  :loading="isRefreshing"
                  @click="handleRefresh"
                >
                  <v-icon size="24">mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <v-sheet color="transparent" class="mt-4">
            <v-card 
              color="white" 
              elevation="2" 
              rounded="xl" 
              class="chat-container"
              :style="{ height: chatHeight }"
            >
              <div 
                ref="messagesContainer" 
                class="messages-area pa-4 pa-md-6"
                @scroll="handleScroll"
              >
                <template v-for="(group, date) in groupedMessages" :key="date">
                  <div class="date-divider my-4">
                    <v-chip
                      size="small"
                      color="#E8EEF5"
                      label
                      class="text-caption font-weight-medium"
                      style="color: #4E5965;"
                    >
                      {{ formatDate(date) }}
                    </v-chip>
                  </div>

                  <div
                    v-for="(message) in group"
                    :key="message._id"
                    class="message-wrapper mb-3"
                    :class="{ 'message-own': isOwnMessage(message) }"
                  >
                    <div class="message-content">
                      <v-avatar
                        v-if="!isOwnMessage(message)"
                        size="36"
                        class="message-avatar flex-shrink-0"
                      >
                        <v-img :src="message.avatar" :alt="message.username" />
                      </v-avatar>

                      <div
                        class="message-bubble"
                        :class="{
                          'message-bubble-own': isOwnMessage(message),
                          'message-bubble-other': !isOwnMessage(message),
                          'message-deleted': message.deleted
                        }"
                      >
                        <div v-if="message.deleted" class="d-flex align-center" style="color: #9AA6B1; font-style: italic;">
                          <v-icon size="16" color="#9AA6B1" class="mr-1">mdi-delete</v-icon>
                          Message deleted
                        </div>

                        <template v-else>
                          <div class="message-text">{{ message.content }}</div>
                          <div class="message-time d-flex align-center justify-end mt-1">
                            <span class="text-caption">{{ message.timestamp }}</span>
                            <v-icon
                              v-if="isOwnMessage(message)"
                              size="14"
                              class="ml-1"
                              :color="message.seen ? '#23A5E6' : '#9AA6B1'"
                            >
                              {{ message.seen ? 'mdi-check-all' : 'mdi-check' }}
                            </v-icon>
                          </div>
                        </template>

                        <div
                          v-if="!message.deleted && isOwnMessage(message)"
                          class="message-actions"
                        >
                          <v-btn
                            icon
                            size="x-small"
                            variant="text"
                            color="white"
                            @click="startEditMessage(message)"
                          >
                            <v-icon size="14">mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            size="x-small"
                            variant="text"
                            color="white"
                            @click="confirmDeleteMessage(message)"
                          >
                            <v-icon size="14">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <v-avatar
                        v-if="isOwnMessage(message)"
                        size="36"
                        class="message-avatar flex-shrink-0"
                      >
                        <v-img :src="message.avatar" :alt="message.username" />
                      </v-avatar>
                    </div>
                  </div>
                </template>

                <div v-if="isTyping" class="message-wrapper mb-3">
                  <div class="message-content">
                    <v-avatar size="36" class="message-avatar flex-shrink-0">
                      <v-img :src="selectedUser?.prependAvatar" :alt="selectedUser?.title" />
                    </v-avatar>
                    <div class="message-bubble message-bubble-other typing-indicator">
                      <div class="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="messages.length === 0 && !showLoader" class="empty-chat">
                  <v-icon size="64" color="#B7BFC7">mdi-message-text-outline</v-icon>
                  <p class="text-h6 mt-4" style="color: #4E5965;">No messages yet</p>
                  <p class="text-body-2" style="color: #9AA6B1;">Start the conversation!</p>
                </div>
              </div>

              <v-divider />
              <div class="message-input-area pa-4">
                <v-slide-y-transition>
                  <v-card
                    v-if="editingMessage"
                    color="#EDF3FD"
                    class="mb-3 pa-3"
                    rounded="lg"
                    elevation="0"
                  >
                    <div class="d-flex align-center">
                      <v-icon size="20" color="#285096" class="mr-2">mdi-pencil</v-icon>
                      <div class="flex-grow-1">
                        <div class="text-caption font-weight-medium" style="color: #285096;">
                          Editing message
                        </div>
                        <div class="text-caption" style="color: #4E5965;">
                          {{ truncateText(editingMessage.content, 50) }}
                        </div>
                      </div>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="#4E5965"
                        @click="cancelEdit"
                      >
                        <v-icon size="18">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-slide-y-transition>

                <div class="d-flex align-center ga-2">
                  <v-textarea
                    v-model="messageInput"
                    :placeholder="editingMessage ? 'Edit your message...' : 'Type a message...'"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details
                    auto-grow
                    rows="1"
                    max-rows="4"
                    color="#23A5E6"
                    class="message-input flex-grow-1"
                    @keydown.enter.exact.prevent="handleSendMessage"
                    @keydown.enter.shift.exact="messageInput += '\n'"
                    @input="handleTyping"
                  >
                    <template #prepend-inner>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="#9AA6B1"
                        @click="showEmojiPicker = !showEmojiPicker"
                      >
                        <v-icon size="20">mdi-emoticon-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-textarea>

                  <v-btn
                    icon
                    size="large"
                    :disabled="!messageInput.trim()"
                    :loading="isSending"
                    style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
                    elevation="2"
                    @click="handleSendMessage"
                  >
                    <v-icon color="white">
                      {{ editingMessage ? 'mdi-check' : 'mdi-send' }}
                    </v-icon>
                  </v-btn>
                </div>

                <div
                  v-if="messageInput.length > 200"
                  class="text-caption text-right mt-1"
                  :style="{ color: messageInput.length > 500 ? '#DC3545' : '#9AA6B1' }"
                >
                  {{ messageInput.length }} / 1000
                </div>
              </div>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card rounded="xl" class="pa-5 text-center">
        <v-avatar color="#DC3545" size="64" variant="tonal" class="mb-4">
          <v-icon size="32" color="#DC3545">mdi-delete-alert</v-icon>
        </v-avatar>
        <h3 class="text-h6 font-weight-semibold mb-2" style="color: #001437;">
          Delete Message?
        </h3>
        <p class="text-body-2 mb-5" style="color: #4E5965; line-height: 1.6;">
          This message will be deleted. This action cannot be undone.
        </p>
        <div class="d-flex justify-center ga-3">
          <v-btn
            variant="outlined"
            color="#4E5965"
            rounded="lg"
            min-width="100"
            class="text-none"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            color="#DC3545"
            rounded="lg"
            min-width="100"
            class="text-none"
            :loading="isDeleting"
            @click="deleteMessage"
          >
            Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="bottom right"
      rounded="lg"
    >
      <div class="d-flex align-center ga-2 font-weight-medium">
        <v-icon size="20">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template #actions>
        <v-btn variant="text" class="text-none" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import moment from 'moment'
import lodash from 'lodash'
import user_avatar from '@/assets/General/user_avatar.png'
import user_avatar2 from '@/assets/General/user_avatar2.png'
import { retrieveBlob } from '@/services/utils'
import {
  getChatOfUser,
  getAllChats,
  postChatMessage,
  getChatById,
  deleteChatById,
  updateChatById
} from '@/controllers/BaseController'

export default {
  name: 'Chat',
  setup() {
    const router = useRouter()

    // Refs
    const messagesContainer = ref(null)
    const showLoader = ref(false)
    const isSending = ref(false)
    const isDeleting = ref(false)
    const isRefreshing = ref(false)
    const isTyping = ref(false)
    const showEmojiPicker = ref(false)
    const deleteDialog = ref(false)

    const selectedUser = ref(null)
    const ownUser = ref(null)
    const messages = ref([])
    const messageInput = ref('')
    const editingMessage = ref(null)
    const messageToDelete = ref(null)
    const apiChatItems = ref([])
    const selectedUserSub = ref(null)

    const snackbar = reactive({
      show: false,
      message: '',
      color: '#28A745',
      icon: 'mdi-check-circle'
    })

    // Computed
    const isAndroid = Capacitor.getPlatform() === 'android'
    const isTablet = computed(() => {
      if (typeof window === 'undefined') return false
      return Math.min(window.screen.width, window.screen.height) >= 600
    })

    const chatHeight = computed(() => {
      if (isAndroid && !isTablet.value) return '75vh'
      return '70vh'
    })

    const groupedMessages = computed(() => {
      const grouped = {}
      
      // Group messages by date
      messages.value.forEach(msg => {
        const date = msg.date || moment().format('YYYY-MM-DD')
        if (!grouped[date]) grouped[date] = []
        grouped[date].push(msg)
      })
      
      // Sort the dates in ascending order (oldest first)
      const sortedGrouped = {}
      Object.keys(grouped)
        .sort((a, b) => new Date(a) - new Date(b))
        .forEach(date => {
          sortedGrouped[date] = grouped[date]
        })
      
      return sortedGrouped
    })

    // Methods
    const showNotification = (message, type = 'success') => {
      snackbar.message = message
      snackbar.color = type === 'success' ? '#28A745' : type === 'error' ? '#DC3545' : '#23A5E6'
      snackbar.icon = type === 'success' ? 'mdi-check-circle' : type === 'error' ? 'mdi-alert-circle' : 'mdi-information'
      snackbar.show = true
    }

    const isOwnMessage = (message) => {
      return message.senderId === ownUser.value?.email
    }

    const formatDate = (dateStr) => {
      const date = moment(dateStr)
      const today = moment().startOf('day')
      const yesterday = moment().subtract(1, 'days').startOf('day')

      if (date.isSame(today, 'day')) return 'Today'
      if (date.isSame(yesterday, 'day')) return 'Yesterday'
      return date.format('MMMM D, YYYY')
    }

    const truncateText = (text, maxLen) => {
      if (!text) return ''
      return text.length > maxLen ? text.substring(0, maxLen) + '...' : text
    }

    const scrollToBottom = async () => {
      await nextTick()
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    const handleScroll = () => {
      
    }

    const handleTyping = lodash.debounce(() => {
      
    }, 300)

    const goBack = () => {
      router.push({ name: 'ChatUsersList' })
    }

    const handleRefresh = async () => {
      isRefreshing.value = true
      try {
        await loadMessages()
        showNotification('Messages refreshed')
      } catch (err) {
        console.error('Refresh failed:', err)
        showNotification('Failed to refresh messages', 'error')
      } finally {
        isRefreshing.value = false
      }
    }

    const getUserSubByEmailSafe = async (email) => {
      const region = 'europe-west10'
      const url = `https://${region}-pia-pinestack-assist.cloudfunctions.net/getUserSubByEmail`

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        })

        if (!res.ok) {
          console.warn('⚠️ Error response from sub lookup')
          return null
        }

        const data = await res.json()
        return data?.sub || null
      } catch (err) {
        console.error('❌ Sub lookup failed:', err)
        return null
      }
    }

    const isDevEnv = () => {
      const platform = Capacitor.getPlatform()
      if (platform === 'android' || platform === 'ios') {
        return !!Capacitor.DEBUG
      }
      const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
      return hostname.includes('localhost') || hostname.startsWith('192.168.')
    }

    const sendNotificationToUser = async (toUserSub, messageContent) => {
      try {
        if (!toUserSub) return

        const payload = {
          toUserId: toUserSub,
          title: '📩 New Message',
          body: messageContent,
          environment: isDevEnv() ? 'development' : 'production',
        }

        const response = await fetch(
          'https://europe-west10-pia-pinestack-assist.cloudfunctions.net/sendDirectNotification',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        )

        if (!response.ok) {
          throw new Error('Notification send failed')
        }
      } catch (err) {
        console.error('🚫 Error sending push notification:', err)
      }
    }

    const handleSendMessage = async () => {
      if (!messageInput.value.trim() || messageInput.value.length > 1000) return

      if (editingMessage.value) {
        await editMessage()
      } else {
        await sendMessage()
      }
    }

    const sendMessage = async () => {
      const content = messageInput.value.trim()
      if (!content) return

      isSending.value = true

      const tempId = `temp-${Date.now()}`
      const newMsg = {
        _id: tempId,
        content: content,
        senderId: ownUser.value.email,
        username: ownUser.value.title,
        avatar: ownUser.value.prependAvatar || user_avatar2,
        date: moment().format('YYYY-MM-DD'),
        timestamp: moment().format('hh:mm A'),
        seen: false,
        deleted: false,
        sending: true
      }

      messages.value.push(newMsg)
      messageInput.value = ''
      await scrollToBottom()

      try {
        const result = await postChatMessage(selectedUser.value?.profile?.id, content)

        const msgIndex = messages.value.findIndex(m => m._id === tempId)
        if (msgIndex !== -1) {
          messages.value[msgIndex]._id = result?.id || tempId
          messages.value[msgIndex].sending = false
          messages.value[msgIndex].seen = true
        }

        if (selectedUserSub.value) {
          await sendNotificationToUser(selectedUserSub.value, content)
        }

        await scrollToBottom()
      } catch (err) {
        console.error('Message send failed:', err)
        const msgIndex = messages.value.findIndex(m => m._id === tempId)
        if (msgIndex !== -1) {
          messages.value.splice(msgIndex, 1)
        }
        showNotification('Failed to send message', 'error')
      } finally {
        isSending.value = false
      }
    }

    const startEditMessage = (message) => {
      editingMessage.value = message
      messageInput.value = message.content
    }

    const cancelEdit = () => {
      editingMessage.value = null
      messageInput.value = ''
    }

    const editMessage = async () => {
      if (!editingMessage.value || !messageInput.value.trim()) return

      isSending.value = true
      const newContent = messageInput.value.trim()

      try {
        const messageIndex = messages.value.findIndex(m => m._id === editingMessage.value._id)
        if (messageIndex !== -1) {
          messages.value[messageIndex].content = newContent
          const chatId = messages.value[messageIndex]._id
          
          const originalChat = lodash.find(apiChatItems.value, chat => chat.id === chatId)
          if (originalChat) {
            originalChat.message = newContent
            await updateChatById(chatId, originalChat)
          }
        }

        showNotification('Message updated')
        cancelEdit()
      } catch (err) {
        console.error('Message edit failed:', err)
        showNotification('Failed to edit message', 'error')
      } finally {
        isSending.value = false
      }
    }

    const confirmDeleteMessage = (message) => {
      messageToDelete.value = message
      deleteDialog.value = true
    }

    const deleteMessage = async () => {
      if (!messageToDelete.value) return

      isDeleting.value = true

      try {
        const messageIndex = messages.value.findIndex(m => m._id === messageToDelete.value._id)
        if (messageIndex !== -1) {
          messages.value[messageIndex].deleted = true
          await deleteChatById(messageToDelete.value._id)
        }

        showNotification('Message deleted')
        deleteDialog.value = false
        messageToDelete.value = null
      } catch (err) {
        console.error('Message deletion failed:', err)
        showNotification('Failed to delete message', 'error')
      } finally {
        isDeleting.value = false
      }
    }

    const loadMessages = async () => {
      try {
        const user = await retrieveBlob('selectedUser')
        const own = await retrieveBlob('ownUser')
        selectedUser.value = user
        ownUser.value = own

        if (!user || !own) {
          showNotification('User data not found', 'error')
          goBack()
          return
        }

        selectedUserSub.value = await getUserSubByEmailSafe(user?.email)

        const chats = await getAllChats()
        const chat = chats.find(
          c => c.userProfileId2 === user?.profile?.id || c.userProfileId1 === user?.profile?.id
        )

        if (chat) {
          const chatData = await getChatById(chat.id)
          const items = chatData.chatItems.items
          apiChatItems.value = items

          // Sort messages by creation date ascending (oldest first, newest last)
          const sortedItems = items.sort((a, b) => 
            new Date(a.createdAt) - new Date(b.createdAt)
          )

          messages.value = sortedItems.map(item => ({
            _id: item.id,
            content: item.message,
            senderId: item.creatorProfile.id === user?.profile?.id ? user?.email : own?.email,
            username: item.creatorProfile.id === own?.profile?.id ? own.title : user.title,
            avatar:
              item.creatorProfile.id === own?.profile?.id
                ? own.prependAvatar || user_avatar2
                : user.prependAvatar || user_avatar,
            date: moment(item.createdAt).format('YYYY-MM-DD'),
            timestamp: moment(item.createdAt).format('hh:mm A'),
            seen: true,
            deleted: false
          }))

          await scrollToBottom()
        }
      } catch (err) {
        console.error('Failed to load messages:', err)
        showNotification('Failed to load messages', 'error')
      }
    }

    onMounted(async () => {
      showLoader.value = true
      try {
        await loadMessages()
      } catch (err) {
        console.error('Chat initialization failed:', err)
        showNotification('Failed to initialize chat', 'error')
      } finally {
        showLoader.value = false
      }
    })

    watch(
      () => messages.value.length,
      () => {
        scrollToBottom()
      }
    )

    return {
      messagesContainer,
      showLoader,
      isSending,
      isDeleting,
      isRefreshing,
      isTyping,
      showEmojiPicker,
      deleteDialog,
      selectedUser,
      ownUser,
      messages,
      messageInput,
      editingMessage,
      messageToDelete,
      snackbar,
      chatHeight,
      groupedMessages,
      isOwnMessage,
      formatDate,
      truncateText,
      handleScroll,
      handleTyping,
      goBack,
      handleRefresh,
      handleSendMessage,
      startEditMessage,
      cancelEdit,
      confirmDeleteMessage,
      deleteMessage
    }
  }
}
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.messages-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
}
.messages-area::-webkit-scrollbar {
  width: 6px;
}

.messages-area::-webkit-scrollbar-track {
  background: transparent;
}

.messages-area::-webkit-scrollbar-thumb {
  background: #D4D9DE;
  border-radius: 3px;
}

.messages-area::-webkit-scrollbar-thumb:hover {
  background: #B7BFC7;
}

/* Date Divider */
.date-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* Message Wrapper */
.message-wrapper {
  display: flex;
  width: 100%;
}

.message-wrapper.message-own {
  justify-content: flex-end;
}

.message-content {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 75%;
}

.message-own .message-content {
  flex-direction: row-reverse;
}

/* Avatar */
.message-avatar {
  border: 2px solid #E8EEF5;
}

/* Message Bubble */
.message-bubble {
  position: relative;
  padding: 10px 14px;
  border-radius: 16px;
  word-wrap: break-word;
  word-break: break-word;
  transition: all 0.2s ease;
}

.message-bubble-own {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-bubble-other {
  background: #F3F6FF;
  color: #001437;
  border-bottom-left-radius: 4px;
  border: 1px solid #E2E8F0;
}

.message-bubble-own .message-time {
  color: rgba(255, 255, 255, 0.85);
}

.message-bubble-other .message-time {
  color: #9AA6B1;
}

.message-deleted {
  opacity: 0.6;
  background: #F8FAFC !important;
  border: 1px dashed #D4D9DE !important;
}

/* Message Text */
.message-text {
  line-height: 1.5;
  white-space: pre-wrap;
}

.message-time {
  font-size: 0.7rem;
  opacity: 0.9;
}

/* Message Actions */
.message-actions {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -70px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-bubble-own:hover .message-actions {
  opacity: 1;
}

.message-actions .v-btn {
  background: rgba(40, 80, 150, 0.9) !important;
  backdrop-filter: blur(10px);
}

/* Typing Indicator */
.typing-indicator {
  padding: 12px 16px;
}

.typing-dots {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  background: #9AA6B1;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

/* Empty Chat */
.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;
}

/* Message Input */
.message-input-area {
  background: #FAFBFC;
  border-top: 1px solid #E8EEF5;
}

.message-input :deep(.v-field) {
  background: white;
}

/* Responsive */
@media (max-width: 960px) {
  .message-content {
    max-width: 85%;
  }

  .message-actions {
    right: auto;
    left: 50%;
    top: auto;
    bottom: -40px;
    transform: translateX(-50%);
  }

  .message-bubble-own:hover .message-actions {
    opacity: 1;
  }
}

@media (max-width: 600px) {
  .messages-area {
    padding: 16px !important;
  }

  .message-input-area {
    padding: 12px !important;
  }

  .message-content {
    max-width: 90%;
  }

  .message-avatar {
    width: 32px !important;
    height: 32px !important;
  }

  .message-bubble {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
}
</style>
